/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { rootOf } from '../internal/events.js';
import { register } from '../registry.js';

/*
 * Two scrollers standing in for `displayedComponents: .hourAndMinute`.
 *
 * Same bargain as the calendar: the `<input type="time">` inside the group is the value the form
 * posts and the control a page with no script keeps, and the wheels are drawn from it and written
 * back to it. The hours offered are the ones the field itself allows, so a wheel cannot land on a
 * time the control would have refused.
 *
 * Reading on a settle rather than on every pixel, because a wheel passes through thirty values on
 * the way to the one that was meant and each of those would otherwise be a change event.
 */

/** How long a wheel has to stop moving before its position is treated as the reader's intent. */
const SETTLE_MILLISECONDS = 120;

/** The fallback row height, for the case where the custom property has not resolved. */
const FALLBACK_ROW = 34;

const settling = new WeakMap<Element, ReturnType<typeof setTimeout>>();

function wheelRow(list: HTMLElement): number {
    return parseFloat(getComputedStyle(list).getPropertyValue('--wp-wheel-row')) || FALLBACK_ROW;
}

export function fillWheel(list: HTMLElement, from: number, to: number): void {
    if (list.children.length > 0) {
        return;
    }

    const options: HTMLLIElement[] = [];
    for (let value = from; value <= to; value++) {
        const option = document.createElement('li');
        option.className = 'wp-wheel-option';
        option.setAttribute('role', 'option');
        option.textContent = String(value).padStart(2, '0');
        options.push(option);
    }

    list.replaceChildren(...options);
}

function markWheel(list: HTMLElement, index: number): void {
    Array.from(list.children).forEach((option, position) => {
        option.setAttribute('aria-selected', String(position === index));
    });
}

export function scrollWheel(list: HTMLElement, index: number): void {
    list.scrollTo({ behavior: 'instant', top: index * wheelRow(list) });
    markWheel(list, index);
}

export function wheelIndex(list: HTMLElement): number {
    const index = Math.round(list.scrollTop / wheelRow(list));
    markWheel(list, index);

    return index;
}

function showTimeWheel(group: Element, value: string): void {
    const display = group.querySelector<HTMLElement>('[data-wp-time-wheel-value]');
    if (display !== null) {
        display.textContent = value;
    }
}

/** The two lists and the field a group is built from, or null when the markup is incomplete. */
function partsOf(group: Element): { hours: HTMLElement; input: HTMLInputElement; minutes: HTMLElement } | null {
    const input = group.querySelector<HTMLInputElement>('[data-wp-time-wheel-input]');
    const hours = group.querySelector<HTMLElement>('[data-wp-time-wheel-hour]');
    const minutes = group.querySelector<HTMLElement>('[data-wp-time-wheel-minute]');
    if (input === null || hours === null || minutes === null) {
        return null;
    }

    return { hours, input, minutes };
}

/** What a list reads at a given index, as the two digits it displays. */
function digitsAt(list: HTMLElement, index: number): string | null {
    return list.children.item(index)?.textContent ?? null;
}

export function paintTimeWheel(group: HTMLDetailsElement): void {
    const parts = partsOf(group);
    if (parts === null) {
        return;
    }

    const { hours, input, minutes } = parts;
    const firstHour = Number((input.min || '00:00').split(':')[0]);
    const lastHour = Number((input.max || '23:00').split(':')[0]);
    const value = (input.value || input.min || '12:00').split(':');
    const hour = value[0] ?? '12';
    const minute = value[1] ?? '00';

    fillWheel(hours, firstHour, Number.isNaN(lastHour) || lastHour < firstHour ? 23 : lastHour);
    fillWheel(minutes, 0, 59);
    group.dataset.enhanced = 'true';
    showTimeWheel(group, `${hour}:${minute}`);

    /* A closed `<details>` has no layout, so nothing inside one can be scrolled into place. The
       wheels are put where they belong when the row opens, which is also the first moment anyone
       can see them - and until then they are sitting at the top of their lists saying the earliest
       hour rather than the chosen one, which is why they must not be read. */
    if (!group.open) {
        group.dataset.positioned = 'false';

        return;
    }

    scrollWheel(hours, Math.min(Math.max(Number(hour) - firstHour, 0), hours.children.length - 1));
    scrollWheel(minutes, Number(minute) || 0);
    group.dataset.positioned = 'true';
}

/** What the band is over right now, without writing it anywhere. */
export function timeWheelValue(group: Element): string {
    const parts = partsOf(group);
    if (parts === null || parts.hours.children.length === 0) {
        return '';
    }

    const hour = digitsAt(parts.hours, wheelIndex(parts.hours));
    const minute = digitsAt(parts.minutes, wheelIndex(parts.minutes));
    if (hour === null || minute === null) {
        return '';
    }

    return `${hour}:${minute}`;
}

export function readTimeWheel(group: HTMLElement): void {
    const parts = partsOf(group);
    if (parts === null || parts.hours.children.length === 0) {
        return;
    }

    /* Wheels that were never laid out are at the top of their lists, which is a time nobody chose.
       Reading them would overwrite the field with the earliest hour on the wheel simply because the
       row was never opened. */
    if (group.dataset.positioned !== 'true') {
        return;
    }

    const value = timeWheelValue(group);
    if (value === '') {
        return;
    }

    showTimeWheel(group, value);

    if (value === parts.input.value) {
        return;
    }

    parts.input.value = value;
    parts.input.dispatchEvent(new Event('change', { bubbles: true }));
}

export function paint(root: ParentNode): void {
    root.querySelectorAll<HTMLDetailsElement>('[data-wp-time-wheel]').forEach(paintTimeWheel);
}

export function listen(): void {
    /* A wheel is a list, so the obvious thing to do with the value two rows above is click it
       rather than drag the one line it is out of place.
       The scroll is instant and not smooth on purpose: the value is read back from where the wheel
       is, and an animation means it is somewhere else for the next few hundred milliseconds -
       during which somebody could press Save and commit the value they were moving away from. */
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const option = target.closest('.wp-wheel-option');
        const list = option?.closest<HTMLElement>('[data-wp-time-wheel-hour], [data-wp-time-wheel-minute]');
        if (option === null || list === null || list === undefined) {
            return;
        }

        const group = list.closest('[data-wp-time-wheel]');
        if (group === null) {
            return;
        }

        scrollWheel(list, Array.from(list.children).indexOf(option));
        showTimeWheel(group, timeWheelValue(group));
    });

    /* Opening lays the wheels out, so they can finally be scrolled to the value; closing is the
       reader saying they are done with them, which is as good a moment to take the value as a
       commit is. */
    document.addEventListener(
        'toggle',
        (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const group = target.closest<HTMLDetailsElement>('[data-wp-time-wheel]');
            if (group === null) {
                return;
            }

            if (group.open) {
                paintTimeWheel(group);
            } else {
                readTimeWheel(group);
            }
        },
        true
    );

    /*
     * Whoever is about to use the value asks for it, rather than the wheel announcing every value
     * it passes through on the way to the one that was meant.
     *
     * Reading on a scroll would mean either `scrollend`, which Safari only learned in 18, or a
     * timer guessing when the wheel has settled - and a value that is right only if a guess was
     * right is not a value. `wp:commit` is raised by the form that is about to be posted, which is
     * exactly the moment the answer has to be true.
     */
    document.addEventListener('wp:commit', (event) => {
        rootOf(event).querySelectorAll<HTMLElement>('[data-wp-time-wheel]').forEach(readTimeWheel);
    });

    /* Only the marking, so a reader is told what the band is over without waiting for a commit.
       The value itself is not written here - see above. */
    document.addEventListener(
        'scroll',
        (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }

            const list = target.closest<HTMLElement>('[data-wp-time-wheel-hour], [data-wp-time-wheel-minute]');
            if (list === null) {
                return;
            }

            const group = list.closest('[data-wp-time-wheel]');
            if (group === null) {
                return;
            }

            clearTimeout(settling.get(list));
            settling.set(
                list,
                setTimeout(() => {
                    wheelIndex(list);
                    showTimeWheel(group, timeWheelValue(group));
                }, SETTLE_MILLISECONDS)
            );
        },
        true
    );

    register(paint);
}
